// ---------------------------------------------------------------------------
// DataHub Service — Real implementation backed by the Teams Eval Data Hub API.
//
// API base path is injected via the IHttpClient's base URL (configured in
// test/datalake-test/datalakeConfig.ts for local dev and production).
//
// Usage:
//   const client = useDataHubClient();
//   const service = useMemo(() => new DataHubService(client), [client]);
// ---------------------------------------------------------------------------

import type { IHttpClient } from '../api/client.js';
import {
  MAX_TEXT_FILE_DOWNLOAD_SIZE_BYTES,
  MEETING_ENTRY_FIELDS,
} from '../constants/index.js';
import { PARTICIPANT_SEARCH_PAGE_SIZE } from '../constants/index.js';
import type { MeetingEntryFieldKey } from '../constants/index.js';

import type {
  AnnotationDataEntry,
  AnnotationDataListResult,
  AnnotationSchema,
  AnnotationSchemaListResult,
  AnnotationSet,
  AnnotationSetListResult,
  AttributeDefinition,
  AttributeDefinitionListResult,
  BatchCompleteFileItem,
  BatchCreateAnnotationDataResult,
  BatchCreateDataResult,
  BatchDownloadUrlItem,
  BatchDownloadUrlResult,
  BatchItemError,
  BatchUploadUrlItem,
  BatchUploadUrlResult,
  CompleteFileUploadRequest,
  CreateAnnotationDataRequest,
  CreateAnnotationSchemaRequest,
  CreateAnnotationSetRequest,
  CreateDataEntryRequest,
  CreateDatasetRequest,
  DataEntry,
  DataEntryListParams,
  DataEntryListResult,
  DataEntryTagSearchResult,
  DataFile,
  Dataset,
  DatasetAccessLevel,
  DatasetCreateDataType,
  DatasetSearchResult,
  DatasetUsageCategory,
  DatasetUsageRecord,
  DatasetUsageResult,
  DeleteParticipantDataResult,
  DatasetTag,
  DatasetTagsResult,
  DatasetVersion,
  DatasetVersionListParams,
  DatasetVersionListResult,
  FileUploadUrlResult,
  GetDatasetUsageParams,
  IDataHubService,
  ParticipantDataSearchResult,
  PerEntryBatchCompleteFileRequest,
  PerEntryBatchUploadUrlResult,
  ListAnnotationDataParams,
  ListAnnotationSchemasParams,
  ListAnnotationSetsParams,
  SearchAttributeDefinitionsParams,
  SearchDataEntriesByParticipantParams,
  SearchDataEntriesByTagsParams,
  SearchDatasetsParams,
  UpdateAnnotationDataRequest,
  UpdateDataEntryRequest,
  UpdateDatasetMetaRequest,
  UpsertDatasetTagsRequest,
} from './dataHubService.interface.js';
import { FILE_TYPE_INT } from './dataHubService.interface.js';
import type { UsageContextOverrides } from './usageContext.interface.js';
import {
  DATASET_USAGE_ACTIONS,
  optionalUsageContextRequestOptions,
  usageContextRequestOptions,
} from './usageContext.js';

// ── Raw API types (Teams Eval Data Hub schema) ────────────────────────────────

type ApiDataSetStatus = 'draft' | 'published' | 'versioned' | 'unknown';
type ApiAccessCategory = 'public' | 'restricted';
type ApiDataType =
  | 'generic'
  | 'meetingData'
  | 'chatData'
  | 'fileData'
  | 'querySetData'
  | 'aiData'
  | 'annotationData';

interface ApiDataSet {
  id: string;
  dataSetId: string;
  name: string;
  description?: string;
  tenantId: string;
  ownerUserObjectId?: string;
  creatorName?: string;
  status: ApiDataSetStatus;
  version: number;
  latestPublishedVersion?: number | null;
  createdDateTime?: string;
  lastUpdatedDateTime?: string;
  tags?: string[];
  accessCategory?: ApiAccessCategory;
  dataType?: ApiDataType;
  sensitivityLabel?: string | null;
  usageRestriction?: string[] | null;
  subType?: string | null;
  source?: string | null;
  retentionOption?: string | null;
  sourceProperties?: Record<string, unknown> | null;
}

interface ApiGenericFile {
  id: string;
  properties?: Record<string, unknown> | null;
  fileName?: string | null;
  fileSizeInBytes?: number | null;
  fileType?: string | null;
  subType?: string | null;
}

interface ApiGenericData {
  id: string;
  dataId?: string;
  name?: string;
  dataType?: string;
  description?: string | null;
  createdDateTime?: string;
  lastUpdatedDateTime?: string;
  version: number;
  properties?: Record<string, unknown>;
  dataFiles?: ApiGenericFile[] | null;
  // Meeting-specific scalar fields (present when dataType is 'meetingData').
  meetingTitle?: string | null;
  language?: string | null;
  meetingDate?: string | null;
  organizerId?: string | null;
}

interface ApiBatchCreateDataFailedItem {
  dataId?: string | null;
  fileName?: string | null;
  errorCode?: string | null;
  errorMessage?: string | null;
  retryable?: boolean;
  canceled?: boolean;
}

interface ApiBatchCreateDataResponse {
  total?: number;
  succeededCount?: number;
  failedCount?: number;
  succeededItems?: ApiGenericData[] | null;
  failedItems?: ApiBatchCreateDataFailedItem[] | null;
}

interface ApiAttributeDefinition {
  id: string;
  attributeDefinitionId: string;
  name?: string | null;
  displayName?: string | null;
  description?: string | null;
  valueType?: string | null;
  allowedValues?: string[] | null;
  isMultiValue: boolean;
  isRequired: boolean;
  isActive: boolean;
}

interface ApiAttributeDefinitionListResponse {
  attributeDefinitions?: ApiAttributeDefinition[] | null;
  continuationToken?: string | null;
  totalCount?: number | null;
  pageSize: number;
}

interface ApiEntityAttributeTagResponse {
  id?: string | null;
  attributeDefinitionId: string;
  attributeName?: string | null;
  values?: string[] | null;
}

interface ApiEntityTagsResponse {
  entityType?: string | null;
  entityId: string;
  tags?: ApiEntityAttributeTagResponse[] | null;
  lastUpdatedDateTime?: string | null;
}

interface ApiFileUploadUrlResponse {
  id: string;
  url: string;
}

interface ApiListDataResponse {
  data?: ApiGenericData[] | null;
  totalCount?: number | null;
  continuationToken?: string | null;
  pageSize: number;
}

interface ApiDataSetVersionHistoryResponse {
  dataSetId?: string | null;
  versions?: ApiDataSet[] | null;
  continuationToken?: string | null;
  totalCount?: number | null;
}

interface ApiDatasetUsageRecord {
  date?: string | null;
  category?: string | null;
  action?: string | null;
  application?: string | null;
  count?: number | null;
  uniqueUsers?: number | null;
}

interface ApiDatasetUsageResponse {
  records?: ApiDatasetUsageRecord[] | null;
}

interface ApiDeleteParticipantDataResponse {
  deletedCount?: number | null;
  deletedDataIds?: string[] | null;
  emailOrAlias?: string | null;
}

interface ApiGetParticipantDataResponse {
  dataSetId?: string | null;
  emailOrAlias?: string | null;
  totalCount?: number | null;
  continuationToken?: string | null;
  pageSize?: number;
  data?: ApiGenericData[] | null;
}

interface ApiSearchResponse {
  dataSets: ApiDataSet[];
  totalCount?: number | null;
  continuationToken?: string | null;
  pageSize: number;
}

interface ApiDataEntryTagSearchResponse {
  results?: { dataEntry?: ApiGenericData }[] | null;
  continuationToken?: string | null;
}

interface ApiBatchFileUploadUrlItem {
  dataId: string;
  files?: ApiFileUploadUrlResponse[] | null;
}

interface ApiBatchFileUploadUrlItemBatchResult {
  total: number;
  succeededCount: number;
  failedCount: number;
  succeededItems?: ApiBatchFileUploadUrlItem[] | null;
  failedItems?: ApiBatchFileFailedItem[] | null;
}

interface ApiBatchFileFailedItem {
  dataId?: string | null;
  fileName?: string | null;
  errorMessage?: string | null;
  retryable: boolean;
}

interface ApiPerEntryBatchUploadUrlResult {
  total: number;
  succeededCount: number;
  failedCount: number;
  succeededItems?: ApiFileUploadUrlResponse[] | null;
  failedItems?: ApiBatchFileFailedItem[] | null;
}

interface ApiBatchFileDownloadUrlItem {
  dataId?: string | null;
  files?: Record<string, string> | null;
}

interface ApiBatchFileDownloadUrlItemBatchResult {
  total: number;
  succeededCount: number;
  failedCount: number;
  succeededItems?: ApiBatchFileDownloadUrlItem[] | null;
  failedItems?: ApiBatchFileFailedItem[] | null;
}

interface ApiAnnotationSchema {
  id: string;
  name?: string | null;
  description?: string | null;
  schema?: string | null;
  createdDateTime?: string | null;
}

interface ApiAnnotationSchemaListResponse {
  schemas?: ApiAnnotationSchema[] | null;
  continuationToken?: string | null;
  pageSize: number;
  totalCount?: number | null;
}

interface ApiAnnotationSet {
  id: string;
  annotationSetId?: string | null;
  dataSetId?: string | null;
  schemaId?: string | null;
  name?: string | null;
  description?: string | null;
  status?: string | null;
  version: number;
  createdDateTime?: string | null;
  lastUpdatedDateTime?: string | null;
  ownerUserObjectId?: string | null;
  publishedAt?: string | null;
  properties?: Record<string, unknown> | null;
}

interface ApiAnnotationSetListResponse {
  annotationSets?: ApiAnnotationSet[] | null;
  continuationToken?: string | null;
  pageSize: number;
  totalCount?: number | null;
}

interface ApiAnnotationData {
  id: string;
  dataId?: string | null;
  dataSetId?: string | null;
  name?: string | null;
  dataType?: string | null;
  rawDataId?: string | null;
  category?: string | null;
  contributor?: string | null;
  payload?: Record<string, unknown> | null;
  schemaId?: string | null;
  description?: string | null;
  properties?: Record<string, unknown> | null;
  version: number;
  createdDateTime?: string | null;
  lastUpdatedDateTime?: string | null;
  dataFiles?: ApiGenericFile[] | null;
}

interface ApiAnnotationDataListResponse {
  data?: ApiAnnotationData[] | null;
  totalCount?: number | null;
  continuationToken?: string | null;
  pageSize: number;
}

interface ApiAnnotationDataBatchResult {
  total?: number;
  succeededCount?: number;
  failedCount?: number;
  succeededItems?: ApiAnnotationData[] | null;
  failedItems?: ApiBatchCreateDataFailedItem[] | null;
}

// ── API path constants ────────────────────────────────────────────────────────

const SEARCH_DATASETS_PATH = 'datasets/search';
const SEARCH_ATTRIBUTE_DEFINITIONS_PATH = 'attributes/search';
const datasetVersionsPath = (id: string) => `datasets/${id}/versions`;
const datasetLatestPath = (id: string) => `datasets/${id}/versions/latest`;
const datasetUsagePath = (id: string) => `datasets/${id}/usage`;
const dataEntriesPath = (id: string) => `datasets/${id}/data`;
const dataEntriesBatchPath = (id: string) => `datasets/${id}/data/batch`;
const datasetTagsPath = (id: string) => `datasets/${id}/tags`;
const datasetDeletePath = (id: string) => `datasets/${id}`;
const datasetHardDeletePath = (id: string) => `datasets/${id}/hard`;
const datasetDraftDeletePath = (id: string) => `datasets/${id}/versions/draft`;
const participantDataPath = (id: string) => `datasets/${id}/participants/data`;
const CREATE_DATASET_PATH = 'datasets';
const dataEntryDetailPath = (dataSetId: string, dataId: string) =>
  `datasets/${dataSetId}/data/${dataId}`;
const dataEntryFileDownloadUrlPath = (
  dataSetId: string,
  dataId: string,
  fileId: string,
) => `datasets/${dataSetId}/data/${dataId}/files/${fileId}/url`;
const dataEntryHardDeletePath = (dataSetId: string, dataId: string) =>
  `datasets/${dataSetId}/data/${dataId}/hard`;
const dataEntryFilePath = (dataSetId: string, dataId: string, fileId: string) =>
  `datasets/${dataSetId}/data/${dataId}/files/${fileId}`;
const dataEntryFilesPath = (dataSetId: string, dataId: string) =>
  `datasets/${dataSetId}/data/${dataId}/files`;
const dataEntryFilesBatchPath = (dataSetId: string, dataId: string) =>
  `datasets/${dataSetId}/data/${dataId}/files/batch`;
const batchFilesPath = (dataSetId: string) =>
  `datasets/${dataSetId}/files/batch`;
const batchDownloadUrlsPath = (dataSetId: string) =>
  `datasets/${dataSetId}/files/batch-download-urls`;
const ANNOTATION_SCHEMAS_PATH = 'annotation-schemas';
const annotationSetsPath = (dataSetId: string) =>
  `datasets/${dataSetId}/annotation-sets`;
const annotationDataPath = (dataSetId: string, annotationSetId: string) =>
  `datasets/${dataSetId}/annotation-sets/${annotationSetId}/data`;
const annotationDataBatchPath = (dataSetId: string, annotationSetId: string) =>
  `datasets/${dataSetId}/annotation-sets/${annotationSetId}/data/batch`;
const annotationDataFilesPath = (
  dataSetId: string,
  annotationSetId: string,
  dataId: string,
) =>
  `datasets/${dataSetId}/annotation-sets/${annotationSetId}/data/${dataId}/files`;
const annotationDataFileUrlsPath = (
  dataSetId: string,
  annotationSetId: string,
  dataId: string,
) =>
  `datasets/${dataSetId}/annotation-sets/${annotationSetId}/data/${dataId}/files/url`;
const annotationSetPath = (dataSetId: string, annotationSetId: string) =>
  `datasets/${dataSetId}/annotation-sets/${annotationSetId}`;
const annotationDataEntryPath = (
  dataSetId: string,
  annotationSetId: string,
  dataId: string,
) => `datasets/${dataSetId}/annotation-sets/${annotationSetId}/data/${dataId}`;
const annotationDataFilePath = (
  dataSetId: string,
  annotationSetId: string,
  dataId: string,
  fileId: string,
) =>
  `datasets/${dataSetId}/annotation-sets/${annotationSetId}/data/${dataId}/files/${fileId}`;
const annotationBatchDownloadUrlsPath = (
  dataSetId: string,
  annotationSetId: string,
) =>
  `datasets/${dataSetId}/annotation-sets/${annotationSetId}/files/batch-download-urls`;

// ── Data-type label ↔ API enum mappings ───────────────────────────────────────

const DATA_TYPE_TO_API: Record<DatasetCreateDataType, ApiDataType> = {
  Generic: 'generic',
  'Meeting Data': 'meetingData',
  'Chat Data': 'chatData',
  'File Data': 'fileData',
  'Annotation Data': 'annotationData',
};

const ACCESS_CATEGORY_TO_API_INT: Record<DatasetAccessLevel, number> = {
  Public: 0,
  Restricted: 1,
};

const DATA_TYPE_TO_API_INT: Record<DatasetCreateDataType, number> = {
  Generic: 0,
  'Meeting Data': 1,
  'Chat Data': 2,
  'File Data': 3,
  'Annotation Data': 4,
};

const API_TO_DATA_TYPE: Partial<Record<ApiDataType, DatasetCreateDataType>> =
  Object.fromEntries(
    Object.entries(DATA_TYPE_TO_API).map(([label, api]) => [api, label]),
  ) as Partial<Record<ApiDataType, DatasetCreateDataType>>;

/**
 * Extracts the meeting-specific scalar fields from a create/update request,
 * omitting any that are unset so non-meeting entries send a clean body.
 */
function meetingEntryBody(
  source: Partial<Record<MeetingEntryFieldKey, string>>,
): Record<string, string> {
  const body: Record<string, string> = {};
  for (const { key } of MEETING_ENTRY_FIELDS) {
    const value = source[key];
    if (value !== undefined) body[key] = value;
  }
  return body;
}

/** Chat-entry scalar fields plus subType, omitting any that are unset. */
function chatEntryBody(
  request: CreateDataEntryRequest,
): Record<string, string> {
  const body: Record<string, string> = {};
  for (const key of ['subType', 'channelId', 'startTime', 'endTime'] as const) {
    const value = request[key];
    if (value !== undefined) body[key] = value;
  }
  return body;
}

/** Reads the meeting-specific scalar fields back off a GET/POST response. */
function meetingEntryFields(
  api: ApiGenericData,
): Pick<
  DataEntry,
  'meetingTitle' | 'language' | 'meetingDate' | 'organizerId'
> {
  return {
    meetingTitle: api.meetingTitle ?? undefined,
    language: api.language ?? undefined,
    meetingDate: api.meetingDate ?? undefined,
    organizerId: api.organizerId ?? undefined,
  };
}

// ── Mapping helpers ───────────────────────────────────────────────────────────

function mapStatus(s: ApiDataSetStatus): Dataset['status'] {
  switch (s) {
    case 'draft':
      return 'Draft';
    case 'published':
      return 'Published';
    case 'versioned':
      return 'Versioned';
    case 'unknown':
      return 'Draft';
  }
}

function mapAccessLevel(
  category: ApiAccessCategory | undefined,
): Dataset['accessLevel'] {
  if (category === 'public') return 'Public';
  if (category === 'restricted') return 'Restricted';
  return undefined;
}

function mapDataSet(api: ApiDataSet): Dataset {
  return {
    // `dataSetId` is the stable business-level ID; fall back to `id`.
    id: api.dataSetId ?? api.id,
    name: api.name,
    description: api.description,
    version: api.version,
    latestPublishedVersion: api.latestPublishedVersion ?? undefined,
    status: mapStatus(api.status),
    createdAt: api.createdDateTime,
    updatedAt: api.lastUpdatedDateTime,
    accessLevel: mapAccessLevel(api.accessCategory),
    dataType: api.dataType != null ? API_TO_DATA_TYPE[api.dataType] : undefined,
    creatorName: api.creatorName ?? undefined,
    tags: api.tags,
    sensitivityLabel: api.sensitivityLabel ?? undefined,
    usageRestriction:
      api.usageRestriction != null && api.usageRestriction.length > 0
        ? api.usageRestriction
        : undefined,
    subType: api.subType ?? undefined,
    source: api.source ?? undefined,
    retentionOption: api.retentionOption ?? undefined,
    sourceProperties: api.sourceProperties ?? undefined,
    // ownerAlias is not in the base API response — requires a separate
    // user-profile lookup by ownerUserObjectId (out of scope for list view).
  };
}

function mapSearchDataSet(api: ApiDataSet): Dataset {
  const dataset = mapDataSet(api);
  const latestPublishedVersion = api.latestPublishedVersion ?? undefined;

  return {
    ...dataset,
    version: latestPublishedVersion ?? dataset.version,
    status: latestPublishedVersion === undefined ? dataset.status : 'Published',
  };
}

function mapDataSetVersion(api: ApiDataSet): DatasetVersion {
  return {
    id: api.dataSetId ?? api.id,
    name: api.name,
    version: api.version,
    status: mapStatus(api.status),
    createdAt: api.createdDateTime,
    updatedAt: api.lastUpdatedDateTime,
  };
}

const USAGE_DEFAULT_CATEGORY: DatasetUsageCategory = 'Read';
const USAGE_UNKNOWN_ACTION = 'Unknown';
const USAGE_UNKNOWN_APPLICATION = 'Unknown';

function mapDatasetUsageRecord(api: ApiDatasetUsageRecord): DatasetUsageRecord {
  return {
    date: api.date ?? '',
    category: (api.category as DatasetUsageCategory) ?? USAGE_DEFAULT_CATEGORY,
    action: api.action ?? USAGE_UNKNOWN_ACTION,
    application: api.application ?? USAGE_UNKNOWN_APPLICATION,
    count: api.count ?? 0,
    uniqueUsers: api.uniqueUsers ?? 0,
  };
}

function mapDatasetTag(api: ApiEntityAttributeTagResponse): DatasetTag {
  return {
    attributeDefinitionId: api.attributeDefinitionId,
    attributeName: api.attributeName ?? api.attributeDefinitionId,
    values: api.values ?? [],
  };
}

function mapEntityTagsResponse(api: ApiEntityTagsResponse): DatasetTagsResult {
  return {
    entityId: api.entityId,
    tags: (api.tags ?? []).map(mapDatasetTag),
  };
}

function mapAttributeDefinition(
  api: ApiAttributeDefinition,
): AttributeDefinition {
  return {
    id: api.id,
    attributeDefinitionId: api.attributeDefinitionId,
    name: api.name ?? api.attributeDefinitionId,
    displayName: api.displayName ?? undefined,
    description: api.description ?? undefined,
    valueType: api.valueType ?? undefined,
    allowedValues: api.allowedValues ?? undefined,
    isMultiValue: api.isMultiValue,
    isRequired: api.isRequired,
    isActive: api.isActive,
  };
}

function mapDataFile(api: ApiGenericFile): DataFile {
  return {
    id: api.id,
    properties: api.properties ?? undefined,
    fileName: api.fileName ?? undefined,
    fileSizeInBytes: api.fileSizeInBytes ?? undefined,
    fileType: api.fileType ?? undefined,
    subType: api.subType ?? undefined,
  };
}

function mapDataEntry(api: ApiGenericData): DataEntry {
  return {
    id: api.id,
    dataId: api.dataId ?? api.id,
    name: api.name,
    dataType: api.dataType,
    description: api.description ?? undefined,
    createdAt: api.createdDateTime,
    updatedAt: api.lastUpdatedDateTime,
    version: api.version,
    properties: api.properties,
    dataFiles: api.dataFiles?.map(mapDataFile),
    ...meetingEntryFields(api),
  };
}

function mapAnnotationSchema(api: ApiAnnotationSchema): AnnotationSchema {
  return {
    id: api.id,
    name: api.name ?? '',
    description: api.description ?? undefined,
    schema: api.schema ?? '',
    createdAt: api.createdDateTime ?? undefined,
  };
}

function mapAnnotationSet(api: ApiAnnotationSet): AnnotationSet {
  return {
    id: api.id,
    annotationSetId: api.annotationSetId ?? api.id,
    dataSetId: api.dataSetId ?? '',
    schemaId: api.schemaId ?? '',
    name: api.name ?? '',
    description: api.description ?? undefined,
    status: mapStatus((api.status ?? 'unknown') as ApiDataSetStatus),
    version: api.version,
    createdAt: api.createdDateTime ?? undefined,
    updatedAt: api.lastUpdatedDateTime ?? undefined,
    ownerUserObjectId: api.ownerUserObjectId ?? undefined,
    publishedAt: api.publishedAt ?? undefined,
    properties: api.properties ?? undefined,
  };
}

function mapAnnotationDataEntry(api: ApiAnnotationData): AnnotationDataEntry {
  return {
    id: api.id,
    dataId: api.dataId ?? api.id,
    dataSetId: api.dataSetId ?? undefined,
    name: api.name ?? undefined,
    dataType: api.dataType ?? undefined,
    rawDataId: api.rawDataId ?? undefined,
    category: api.category ?? undefined,
    contributor: api.contributor ?? undefined,
    payload: api.payload ?? undefined,
    schemaId: api.schemaId ?? undefined,
    description: api.description ?? undefined,
    properties: api.properties ?? undefined,
    version: api.version,
    createdAt: api.createdDateTime ?? undefined,
    updatedAt: api.lastUpdatedDateTime ?? undefined,
    dataFiles: api.dataFiles?.map(mapDataFile),
  };
}

function mapBatchFailure(item: ApiBatchCreateDataFailedItem): BatchItemError {
  return {
    dataId: item.dataId ?? undefined,
    errorCode: item.errorCode ?? undefined,
    errorMessage: item.errorMessage ?? undefined,
  };
}

// ── Service class ─────────────────────────────────────────────────────────────

export class DataHubService implements IDataHubService {
  constructor(private readonly client: IHttpClient) {}

  async searchDatasets(
    params: SearchDatasetsParams,
  ): Promise<DatasetSearchResult> {
    const { data } = await this.client.post<ApiSearchResponse>(
      SEARCH_DATASETS_PATH,
      {
        query: params.query?.trim() || undefined, // eslint-disable-line @typescript-eslint/prefer-nullish-coalescing
        ownerUserObjectId: params.ownerUserObjectId,
        accessCategory: params.accessCategory
          ? ACCESS_CATEGORY_TO_API_INT[params.accessCategory]
          : undefined,
        dataType: params.dataType
          ? DATA_TYPE_TO_API_INT[params.dataType]
          : undefined,
        pageSize: params.pageSize ?? 20,
        continuationToken: params.continuationToken,
        includeCount: params.includeCount ?? true,
      },
    );

    return {
      items: data.dataSets.map(mapSearchDataSet),
      totalCount: data.totalCount ?? null,
      continuationToken: data.continuationToken ?? null,
    };
  }

  async createDataset(
    request: CreateDatasetRequest,
    usage?: UsageContextOverrides,
  ): Promise<Dataset> {
    const { data } = await this.client.post<ApiDataSet>(
      CREATE_DATASET_PATH,
      {
        name: request.name,
        description: request.description,
        accessCategory:
          request.accessCategory.toLowerCase() as ApiAccessCategory,
        dataType: DATA_TYPE_TO_API[request.dataType],
        sensitivityLabel: request.sensitivityLabel,
        usageRestriction: request.usageRestriction
          ? [request.usageRestriction]
          : undefined,
        subType: request.subType,
        source: request.source,
        retentionOption: request.retentionOption,
        sourceProperties: request.sourceProperties ?? undefined,
      },
      optionalUsageContextRequestOptions(
        DATASET_USAGE_ACTIONS.CREATE_DATASET,
        usage,
      ),
    );
    return mapDataSet(data);
  }

  async getDataset(dataSetId: string): Promise<Dataset> {
    const { data } = await this.client.get<ApiDataSet>(
      datasetLatestPath(dataSetId),
    );
    return mapDataSet(data);
  }

  async updateDatasetMeta(
    dataSetId: string,
    request: UpdateDatasetMetaRequest,
    usage?: UsageContextOverrides,
  ): Promise<Dataset> {
    const { data } = await this.client.put<ApiDataSet>(
      `datasets/${dataSetId}`,
      {
        name: request.name,
        description: request.description,
        source: request.source,
        usageRestriction: request.usageRestriction
          ? [request.usageRestriction]
          : undefined,
        sourceProperties: request.sourceProperties ?? undefined,
      },
      optionalUsageContextRequestOptions(
        DATASET_USAGE_ACTIONS.UPDATE_DATASET,
        usage,
      ),
    );
    return mapDataSet(data);
  }

  async publishDataset(
    dataSetId: string,
    usage?: UsageContextOverrides,
  ): Promise<Dataset> {
    const { data } = await this.client.patch<ApiDataSet>(
      `datasets/${dataSetId}/publish`,
      undefined,
      usageContextRequestOptions(DATASET_USAGE_ACTIONS.PUBLISH_VERSION, usage),
    );
    return mapDataSet(data);
  }

  async createDraftDataset(
    dataSetId: string,
    usage?: UsageContextOverrides,
  ): Promise<Dataset> {
    const { data } = await this.client.post<ApiDataSet>(
      `datasets/${dataSetId}/versions/draft`,
      undefined,
      optionalUsageContextRequestOptions(
        DATASET_USAGE_ACTIONS.CREATE_DRAFT,
        usage,
      ),
    );
    return mapDataSet(data);
  }

  async listDatasetVersions(
    params: DatasetVersionListParams,
  ): Promise<DatasetVersionListResult> {
    const query = new URLSearchParams({
      pageSize: String(params.pageSize ?? 20),
      includeCount: String(params.includeCount ?? true),
    });
    if (params.continuationToken) {
      query.set('continuationToken', params.continuationToken);
    }
    const { data } = await this.client.get<ApiDataSetVersionHistoryResponse>(
      `${datasetVersionsPath(params.dataSetId)}?${query.toString()}`,
    );
    return {
      items: (data.versions ?? []).map(mapDataSetVersion),
      totalCount: data.totalCount ?? null,
      continuationToken: data.continuationToken ?? null,
    };
  }

  async getDatasetUsage(
    params: GetDatasetUsageParams,
  ): Promise<DatasetUsageResult> {
    const query = new URLSearchParams();
    if (params.from) query.set('from', params.from);
    if (params.to) query.set('to', params.to);
    if (params.groupBy) query.set('groupBy', params.groupBy);
    if (params.category) query.set('category', params.category);
    if (params.application) query.set('application', params.application);
    const queryString = query.toString();
    const url = queryString
      ? `${datasetUsagePath(params.dataSetId)}?${queryString}`
      : datasetUsagePath(params.dataSetId);
    const { data } = await this.client.get<ApiDatasetUsageResponse>(url);
    return {
      records: (data.records ?? []).map(mapDatasetUsageRecord),
    };
  }

  async searchAttributeDefinitions(
    params: SearchAttributeDefinitionsParams,
  ): Promise<AttributeDefinitionListResult> {
    const { data } = await this.client.post<ApiAttributeDefinitionListResponse>(
      SEARCH_ATTRIBUTE_DEFINITIONS_PATH,
      {
        isActive: params.isActive,
        pageSize: params.pageSize ?? 100,
        continuationToken: params.continuationToken,
        includeCount: params.includeCount ?? false,
      },
    );

    return {
      items: (data.attributeDefinitions ?? []).map(mapAttributeDefinition),
      totalCount: data.totalCount ?? null,
      continuationToken: data.continuationToken ?? null,
    };
  }

  async getDatasetTags(dataSetId: string): Promise<DatasetTagsResult> {
    const { data } = await this.client.get<ApiEntityTagsResponse>(
      datasetTagsPath(dataSetId),
    );
    return mapEntityTagsResponse(data);
  }

  async upsertDatasetTags(
    dataSetId: string,
    request: UpsertDatasetTagsRequest,
    usage?: UsageContextOverrides,
  ): Promise<DatasetTagsResult> {
    const { data } = await this.client.put<ApiEntityTagsResponse>(
      datasetTagsPath(dataSetId),
      { tags: request.tags },
      optionalUsageContextRequestOptions(
        DATASET_USAGE_ACTIONS.SAVE_ATTRIBUTES,
        usage,
      ),
    );
    return mapEntityTagsResponse(data);
  }

  async deleteDataset(
    dataSetId: string,
    usage?: UsageContextOverrides,
  ): Promise<void> {
    await this.client.delete(
      datasetDeletePath(dataSetId),
      undefined,
      optionalUsageContextRequestOptions(
        DATASET_USAGE_ACTIONS.DELETE_DATASET,
        usage,
      ),
    );
  }

  async hardDeleteDataset(
    dataSetId: string,
    usage?: UsageContextOverrides,
  ): Promise<void> {
    await this.client.delete(
      datasetHardDeletePath(dataSetId),
      undefined,
      optionalUsageContextRequestOptions(
        DATASET_USAGE_ACTIONS.DELETE_DATASET,
        usage,
      ),
    );
  }

  async deleteDatasetDraft(
    dataSetId: string,
    usage?: UsageContextOverrides,
  ): Promise<void> {
    await this.client.delete(
      datasetDraftDeletePath(dataSetId),
      undefined,
      optionalUsageContextRequestOptions(
        DATASET_USAGE_ACTIONS.DELETE_DATASET,
        usage,
      ),
    );
  }

  async hardDeleteByParticipant(
    dataSetId: string,
    emailOrAlias: string,
    usage?: UsageContextOverrides,
  ): Promise<DeleteParticipantDataResult> {
    const query = new URLSearchParams({ emailOrAlias });
    const { data } = await this.client.delete<ApiDeleteParticipantDataResponse>(
      `${participantDataPath(dataSetId)}?${query.toString()}`,
      undefined,
      optionalUsageContextRequestOptions(
        DATASET_USAGE_ACTIONS.DELETE_PARTICIPANT_DATA,
        usage,
      ),
    );
    return {
      deletedCount: data.deletedCount ?? 0,
      deletedDataIds: data.deletedDataIds ?? [],
      emailOrAlias: data.emailOrAlias ?? null,
    };
  }

  async searchDataEntriesByParticipant(
    params: SearchDataEntriesByParticipantParams,
  ): Promise<ParticipantDataSearchResult> {
    const query = new URLSearchParams({
      emailOrAlias: params.emailOrAlias,
      pageSize: String(params.pageSize ?? PARTICIPANT_SEARCH_PAGE_SIZE),
      includeCount: String(params.includeCount ?? true),
    });
    if (params.continuationToken) {
      query.set('continuationToken', params.continuationToken);
    }
    const { data } = await this.client.get<ApiGetParticipantDataResponse>(
      `${participantDataPath(params.dataSetId)}?${query.toString()}`,
    );
    return {
      items: (data.data ?? []).map(mapDataEntry),
      totalCount: data.totalCount ?? null,
      continuationToken: data.continuationToken ?? null,
    };
  }

  async getDataEntry(
    dataSetId: string,
    dataId: string,
    dataSetVersion: number,
  ): Promise<DataEntry> {
    const query = new URLSearchParams({
      dataSetVersion: String(dataSetVersion),
    });
    const { data } = await this.client.get<ApiGenericData>(
      `${dataEntryDetailPath(dataSetId, dataId)}?${query.toString()}`,
    );
    return mapDataEntry(data);
  }

  async getDataEntryFileDownloadUrl(
    dataSetId: string,
    dataId: string,
    fileId: string,
    dataSetVersion: number,
    usage?: UsageContextOverrides,
  ): Promise<string> {
    const query = new URLSearchParams({
      dataSetVersion: String(dataSetVersion),
    });
    const { data } = await this.client.get<string>(
      `${dataEntryFileDownloadUrlPath(dataSetId, dataId, fileId)}?${query.toString()}`,
      optionalUsageContextRequestOptions(
        DATASET_USAGE_ACTIONS.DOWNLOAD_DATASET,
        usage,
      ),
    );
    return data;
  }

  async readTextFileFromBlob(
    downloadUrl: string,
    signal?: AbortSignal,
    maxBytes = MAX_TEXT_FILE_DOWNLOAD_SIZE_BYTES,
  ): Promise<string> {
    const useProxy = import.meta.env.DEV && import.meta.env.MODE !== 'test';
    const requestUrl = useProxy
      ? `/__adls-proxy?url=${encodeURIComponent(downloadUrl)}`
      : downloadUrl;
    const response = await fetch(requestUrl, { signal });
    if (!response.ok) {
      throw new Error(`ADLS download failed: ${response.status}`);
    }

    const declaredLength = Number(response.headers.get('Content-Length'));
    if (Number.isFinite(declaredLength) && declaredLength > maxBytes) {
      throw new Error('The selected file is too large to parse.');
    }

    if (response.body === null) {
      return '';
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let totalBytes = 0;
    let source = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        break;
      }

      totalBytes += value.byteLength;
      if (totalBytes > maxBytes) {
        await reader.cancel();
        throw new Error('The selected file is too large to parse.');
      }
      source += decoder.decode(value, { stream: true });
    }

    return source + decoder.decode();
  }

  async deleteDataEntryFile(
    dataSetId: string,
    dataId: string,
    fileId: string,
    usage?: UsageContextOverrides,
  ): Promise<void> {
    await this.client.delete(
      dataEntryFilePath(dataSetId, dataId, fileId),
      undefined,
      optionalUsageContextRequestOptions(
        DATASET_USAGE_ACTIONS.DELETE_DATA_ENTRY_FILE,
        usage,
      ),
    );
  }

  async listDataEntries(
    params: DataEntryListParams,
  ): Promise<DataEntryListResult> {
    const query = new URLSearchParams({
      dataSetVersion: String(params.dataSetVersion),
      pageSize: String(params.pageSize ?? 10),
      includeCount: String(params.includeCount ?? true),
    });
    if (params.continuationToken) {
      query.set('continuationToken', params.continuationToken);
    }
    const { data } = await this.client.get<ApiListDataResponse>(
      `${dataEntriesPath(params.dataSetId)}?${query.toString()}`,
    );
    return {
      items: (data.data ?? []).map(mapDataEntry),
      totalCount: data.totalCount ?? null,
      continuationToken: data.continuationToken ?? null,
    };
  }

  async searchDataEntriesByTags(
    params: SearchDataEntriesByTagsParams,
  ): Promise<DataEntryTagSearchResult> {
    const { data } = await this.client.post<ApiDataEntryTagSearchResponse>(
      `${dataEntriesPath(params.dataSetId)}/search/by-tags`,
      {
        filters: params.filters,
        matchMode: params.matchMode,
        pageSize: params.pageSize ?? 20,
        continuationToken: params.continuationToken ?? null,
      },
    );
    return {
      items: (data.results ?? [])
        .filter((r): r is { dataEntry: ApiGenericData } => r.dataEntry != null)
        .map((r) => mapDataEntry(r.dataEntry)),
      continuationToken: data.continuationToken ?? null,
    };
  }

  async updateDataEntry(
    dataSetId: string,
    request: UpdateDataEntryRequest,
    usage?: UsageContextOverrides,
  ): Promise<DataEntry> {
    const displayType = request.dataType
      ? (API_TO_DATA_TYPE[request.dataType as ApiDataType] ?? undefined)
      : undefined;
    const apiInt = displayType ? DATA_TYPE_TO_API_INT[displayType] : 0;

    const { data } = await this.client.put<ApiGenericData>(
      dataEntriesPath(dataSetId),
      {
        dataId: request.dataId,
        dataType: apiInt,
        name: request.name,
        description: request.description,
        properties: request.properties,
        ...meetingEntryBody(request),
      },
      usageContextRequestOptions(
        DATASET_USAGE_ACTIONS.UPDATE_DATA_ENTRY,
        usage,
      ),
    );
    return mapDataEntry(data);
  }

  async deleteDataEntry(
    dataSetId: string,
    dataId: string,
    usage?: UsageContextOverrides,
  ): Promise<void> {
    await this.client.delete(
      dataEntryDetailPath(dataSetId, dataId),
      undefined,
      optionalUsageContextRequestOptions(
        DATASET_USAGE_ACTIONS.DELETE_DATA_ENTRY,
        usage,
      ),
    );
  }

  async hardDeleteDataEntry(
    dataSetId: string,
    dataId: string,
    usage?: UsageContextOverrides,
  ): Promise<void> {
    await this.client.delete(
      dataEntryHardDeletePath(dataSetId, dataId),
      undefined,
      optionalUsageContextRequestOptions(
        DATASET_USAGE_ACTIONS.DELETE_DATA_ENTRY,
        usage,
      ),
    );
  }

  async createDataEntry(
    dataSetId: string,
    request: CreateDataEntryRequest,
    usage?: UsageContextOverrides,
  ): Promise<DataEntry> {
    const { data } = await this.client.post<ApiGenericData>(
      dataEntriesPath(dataSetId),
      {
        name: request.name,
        dataType: DATA_TYPE_TO_API[request.dataType],
        description: request.description,
        properties: request.properties,
        ...chatEntryBody(request),
        ...meetingEntryBody(request),
      },
      usageContextRequestOptions(
        DATASET_USAGE_ACTIONS.CREATE_DATA_ENTRY,
        usage,
      ),
    );
    return mapDataEntry(data);
  }

  async batchAddDataEntries(
    dataSetId: string,
    entries: CreateDataEntryRequest[],
    usage?: UsageContextOverrides,
  ): Promise<BatchCreateDataResult> {
    const body = entries.map((e) => ({
      name: e.name,
      dataType: DATA_TYPE_TO_API[e.dataType],
      description: e.description,
      properties: e.properties,
      ...chatEntryBody(e),
      ...meetingEntryBody(e),
    }));
    const { data } = await this.client.post<ApiBatchCreateDataResponse>(
      dataEntriesBatchPath(dataSetId),
      body,
      // Opt-in: this method is shared with synthetic-data generation, which is
      // not a tracked dataset action. Only the folder-upload caller passes
      // `usage`, tagging its single batch-create as one Upload Data event (D2).
      optionalUsageContextRequestOptions(
        DATASET_USAGE_ACTIONS.UPLOAD_DATASET,
        usage,
      ),
    );
    return {
      succeeded: (data.succeededItems ?? []).map(mapDataEntry),
      failed: (data.failedItems ?? []).map(mapBatchFailure),
    };
  }

  async generateFileUploadUrl(
    dataSetId: string,
    dataId: string,
    fileName: string,
    contentType?: string,
    usage?: UsageContextOverrides,
  ): Promise<FileUploadUrlResult> {
    const { data } = await this.client.post<ApiFileUploadUrlResponse>(
      dataEntryFilesPath(dataSetId, dataId),
      { fileName, contentType },
      optionalUsageContextRequestOptions(
        DATASET_USAGE_ACTIONS.UPLOAD_FILE,
        usage,
      ),
    );
    return { fileId: data.id, uploadUrl: data.url };
  }

  async uploadFileToBlob(uploadUrl: string, file: File): Promise<void> {
    // In the dev server, route through the Vite ADLS proxy to avoid CORS.
    const useProxy = import.meta.env.DEV && import.meta.env.MODE !== 'test';
    const adlsUrl = (target: string) =>
      useProxy ? `/__adls-proxy?url=${encodeURIComponent(target)}` : target;

    // ADLS Gen2 3-step upload: create → append → flush
    const createRes = await fetch(adlsUrl(`${uploadUrl}&resource=file`), {
      method: 'PUT',
    });
    if (!createRes.ok) {
      throw new Error(`ADLS create failed: ${createRes.status}`);
    }

    const appendRes = await fetch(
      adlsUrl(`${uploadUrl}&action=append&position=0`),
      {
        method: 'PATCH',
        body: file,
        headers: { 'Content-Type': 'application/octet-stream' },
      },
    );
    if (!appendRes.ok) {
      throw new Error(`ADLS append failed: ${appendRes.status}`);
    }

    const flushRes = await fetch(
      adlsUrl(`${uploadUrl}&action=flush&position=${file.size}`),
      {
        method: 'PATCH',
        body: new Blob([]),
      },
    );
    if (!flushRes.ok) {
      throw new Error(`ADLS flush failed: ${flushRes.status}`);
    }
  }

  async completeFileUpload(
    dataSetId: string,
    dataId: string,
    request: CompleteFileUploadRequest,
    usage?: UsageContextOverrides,
  ): Promise<DataEntry> {
    const { data } = await this.client.put<ApiGenericData>(
      dataEntryFilesPath(dataSetId, dataId),
      {
        id: request.id,
        fileName: request.fileName,
        fileType: request.fileType,
        fileSizeInBytes: request.fileSizeInBytes,
      },
      optionalUsageContextRequestOptions(
        DATASET_USAGE_ACTIONS.UPLOAD_FILE,
        usage,
      ),
    );
    return mapDataEntry(data);
  }

  async batchGenerateFileUploadUrls(
    dataSetId: string,
    dataId: string,
    fileNames: string[],
  ): Promise<PerEntryBatchUploadUrlResult> {
    const { data } = await this.client.post<ApiPerEntryBatchUploadUrlResult>(
      dataEntryFilesBatchPath(dataSetId, dataId),
      fileNames.map((fileName) => ({ fileName })),
    );
    return {
      total: data.total,
      succeededCount: data.succeededCount,
      failedCount: data.failedCount,
      succeededItems: (data.succeededItems ?? []).map((f) => ({
        fileId: f.id,
        uploadUrl: f.url,
      })),
      failedItems: (data.failedItems ?? []).map((f) => ({
        dataId: f.dataId ?? undefined,
        fileName: f.fileName ?? undefined,
        errorMessage: f.errorMessage ?? undefined,
        retryable: f.retryable,
      })),
    };
  }

  async batchCompleteFileUploads(
    dataSetId: string,
    dataId: string,
    requests: PerEntryBatchCompleteFileRequest[],
  ): Promise<DataEntry> {
    const { data } = await this.client.put<ApiGenericData>(
      dataEntryFilesBatchPath(dataSetId, dataId),
      requests.map((r) => ({
        id: r.id,
        fileType: FILE_TYPE_INT[r.fileType],
        subType: r.subType,
        fileName: r.fileName,
        fileSizeInBytes: r.fileSizeInBytes,
      })),
    );
    return mapDataEntry(data);
  }

  // ── Annotation schemas ──────────────────────────────────────────────────

  async listAnnotationSchemas(
    params: ListAnnotationSchemasParams = {},
  ): Promise<AnnotationSchemaListResult> {
    const query = new URLSearchParams();
    if (params.nameContains) query.set('nameContains', params.nameContains);
    query.set('pageSize', String(params.pageSize ?? 100));
    if (params.continuationToken) {
      query.set('continuationToken', params.continuationToken);
    }
    if (params.includeCount) query.set('includeCount', 'true');
    const { data } = await this.client.get<ApiAnnotationSchemaListResponse>(
      `${ANNOTATION_SCHEMAS_PATH}?${query.toString()}`,
    );
    return {
      items: (data.schemas ?? []).map(mapAnnotationSchema),
      totalCount: data.totalCount ?? null,
      continuationToken: data.continuationToken ?? null,
    };
  }

  async createAnnotationSchema(
    request: CreateAnnotationSchemaRequest,
  ): Promise<AnnotationSchema> {
    const { data } = await this.client.post<ApiAnnotationSchema>(
      ANNOTATION_SCHEMAS_PATH,
      {
        name: request.name,
        description: request.description,
        schema: request.schema,
      },
    );
    return mapAnnotationSchema(data);
  }

  // ── Annotation sets ─────────────────────────────────────────────────────

  async listAnnotationSets(
    params: ListAnnotationSetsParams,
  ): Promise<AnnotationSetListResult> {
    const query = new URLSearchParams({
      pageSize: String(params.pageSize ?? 20),
      includeCount: String(params.includeCount ?? true),
    });
    if (params.continuationToken) {
      query.set('continuationToken', params.continuationToken);
    }
    const { data } = await this.client.get<ApiAnnotationSetListResponse>(
      `${annotationSetsPath(params.dataSetId)}?${query.toString()}`,
    );
    return {
      items: (data.annotationSets ?? []).map(mapAnnotationSet),
      totalCount: data.totalCount ?? null,
      continuationToken: data.continuationToken ?? null,
    };
  }

  async createAnnotationSet(
    dataSetId: string,
    request: CreateAnnotationSetRequest,
    usage?: UsageContextOverrides,
  ): Promise<AnnotationSet> {
    const { data } = await this.client.post<ApiAnnotationSet>(
      annotationSetsPath(dataSetId),
      {
        schemaId: request.schemaId,
        name: request.name,
        description: request.description,
        properties: request.properties,
      },
      // Always-emit: creating an annotation set is 1:1 with a deliberate user
      // action. Usage rolls up to the parent dataset via the URL path (R1).
      usageContextRequestOptions(
        DATASET_USAGE_ACTIONS.CREATE_ANNOTATION_SET,
        usage,
      ),
    );
    return mapAnnotationSet(data);
  }

  async updateAnnotationSet(
    dataSetId: string,
    annotationSetId: string,
    request: { name?: string; description?: string },
    usage?: UsageContextOverrides,
  ): Promise<AnnotationSet> {
    const { data } = await this.client.put<ApiAnnotationSet>(
      annotationSetPath(dataSetId, annotationSetId),
      request,
      optionalUsageContextRequestOptions(
        DATASET_USAGE_ACTIONS.UPDATE_ANNOTATION_SET,
        usage,
      ),
    );
    return mapAnnotationSet(data);
  }

  async publishAnnotationSet(
    dataSetId: string,
    annotationSetId: string,
    usage?: UsageContextOverrides,
  ): Promise<AnnotationSet> {
    const { data } = await this.client.patch<ApiAnnotationSet>(
      `${annotationSetPath(dataSetId, annotationSetId)}/publish`,
      {},
      // Always-emit: publishing an annotation set is a deliberate user action.
      usageContextRequestOptions(
        DATASET_USAGE_ACTIONS.PUBLISH_ANNOTATION_SET,
        usage,
      ),
    );
    return mapAnnotationSet(data);
  }

  /** Create a Draft explicitly. A conflict may indicate any lifecycle conflict. */
  async createAnnotationSetDraft(
    dataSetId: string,
    annotationSetId: string,
  ): Promise<AnnotationSet> {
    const { data } = await this.client.post<ApiAnnotationSet>(
      `${annotationSetPath(dataSetId, annotationSetId)}/versions/draft`,
      {},
    );
    return mapAnnotationSet(data);
  }

  async getAnnotationSetVersion(
    dataSetId: string,
    annotationSetId: string,
    version: number | 'draft',
  ): Promise<AnnotationSet> {
    const { data } = await this.client.get<ApiAnnotationSet>(
      `${annotationSetPath(dataSetId, annotationSetId)}/versions/${version}`,
    );
    return mapAnnotationSet(data);
  }

  async annotationReadSourceItems(
    dataSetId: string,
    annotationSetId: string,
    version: number,
    rawDataIds: string[],
  ): Promise<AnnotationDataEntry[]> {
    const { data, headers } =
      await this.client.post<ApiAnnotationDataBatchResult>(
        `${annotationDataPath(dataSetId, annotationSetId)}/batch-get-by-raw-data-ids`,
        { version: String(version), rawDataIds },
      );
    if (headers?.get('X-Annotation-Set-Version') !== String(version)) {
      throw new Error(
        'TEDH did not acknowledge the selected annotation version. Editing is unavailable.',
      );
    }
    if ((data.failedItems?.length ?? 0) > 0) {
      throw new Error('Some annotation items could not be read.');
    }
    return (data.succeededItems ?? []).map(mapAnnotationDataEntry);
  }

  async deleteAnnotationSet(
    dataSetId: string,
    annotationSetId: string,
    usage?: UsageContextOverrides,
  ): Promise<void> {
    await this.client.delete(
      annotationSetPath(dataSetId, annotationSetId),
      undefined,
      optionalUsageContextRequestOptions(
        DATASET_USAGE_ACTIONS.DELETE_ANNOTATION_SET,
        usage,
      ),
    );
  }

  async deleteAnnotationSetDraft(
    dataSetId: string,
    annotationSetId: string,
    usage?: UsageContextOverrides,
  ): Promise<void> {
    await this.client.delete(
      `${annotationSetPath(dataSetId, annotationSetId)}/versions/draft`,
      undefined,
      optionalUsageContextRequestOptions(
        DATASET_USAGE_ACTIONS.DELETE_ANNOTATION_SET,
        usage,
      ),
    );
  }

  async hardDeleteAnnotationSet(
    dataSetId: string,
    annotationSetId: string,
    usage?: UsageContextOverrides,
  ): Promise<void> {
    await this.client.delete(
      `${annotationSetPath(dataSetId, annotationSetId)}/hard`,
      undefined,
      optionalUsageContextRequestOptions(
        DATASET_USAGE_ACTIONS.DELETE_ANNOTATION_SET,
        usage,
      ),
    );
  }

  // ── Annotation data ─────────────────────────────────────────────────────

  async listAnnotationData(
    params: ListAnnotationDataParams,
  ): Promise<AnnotationDataListResult> {
    const query = new URLSearchParams({
      pageSize: String(params.pageSize ?? 20),
      includeCount: String(params.includeCount ?? true),
    });
    if (params.category) query.set('category', params.category);
    if (params.contributor) query.set('contributor', params.contributor);
    if (params.continuationToken) {
      query.set('continuationToken', params.continuationToken);
    }
    const { data } = await this.client.get<ApiAnnotationDataListResponse>(
      `${annotationDataPath(params.dataSetId, params.annotationSetId)}?${query.toString()}`,
    );
    return {
      items: (data.data ?? []).map(mapAnnotationDataEntry),
      totalCount: data.totalCount ?? null,
      continuationToken: data.continuationToken ?? null,
    };
  }

  async batchCreateAnnotationData(
    dataSetId: string,
    annotationSetId: string,
    items: CreateAnnotationDataRequest[],
    usage?: UsageContextOverrides,
  ): Promise<BatchCreateAnnotationDataResult> {
    const { data } = await this.client.post<ApiAnnotationDataBatchResult>(
      annotationDataBatchPath(dataSetId, annotationSetId),
      items,
      // Opt-in: a folder upload calls this once per subfolder. The orchestrator
      // passes `usage` on a single representative call so the whole bulk upload
      // records exactly one Upload Annotations event (D2). Annotation usage
      // rolls up to the parent dataset via the URL path (R1).
      optionalUsageContextRequestOptions(
        DATASET_USAGE_ACTIONS.UPLOAD_ANNOTATIONS,
        usage,
      ),
    );
    return {
      total: data.total ?? 0,
      succeededCount: data.succeededCount ?? 0,
      failedCount: data.failedCount ?? 0,
      succeededItems: (data.succeededItems ?? []).map(mapAnnotationDataEntry),
      failedItems: (data.failedItems ?? []).map(mapBatchFailure),
    };
  }

  async createAnnotationData(
    dataSetId: string,
    annotationSetId: string,
    request: CreateAnnotationDataRequest,
    usage?: UsageContextOverrides,
  ): Promise<AnnotationDataEntry> {
    const { data } = await this.client.post<ApiAnnotationData>(
      annotationDataPath(dataSetId, annotationSetId),
      request,
      // Always-emit: a single annotation creation is 1:1 with a deliberate user
      // action, mirroring createDataEntry. Annotation usage rolls up to the
      // parent dataset via the URL path (R1).
      usageContextRequestOptions(
        DATASET_USAGE_ACTIONS.CREATE_ANNOTATION,
        usage,
      ),
    );
    return mapAnnotationDataEntry(data);
  }

  async updateAnnotationData(
    dataSetId: string,
    annotationSetId: string,
    dataId: string,
    request: UpdateAnnotationDataRequest,
    usage?: UsageContextOverrides,
  ): Promise<AnnotationDataEntry> {
    const { data } = await this.client.put<ApiAnnotationData>(
      annotationDataEntryPath(dataSetId, annotationSetId, dataId),
      {
        dataId: request.dataId,
        dataType: request.dataType,
        name: request.name,
        subType: request.subType,
        description: request.description,
        properties: request.properties,
      },
      // Always-emit: a single annotation update is 1:1 with a deliberate user
      // action, mirroring updateDataEntry.
      usageContextRequestOptions(
        DATASET_USAGE_ACTIONS.UPDATE_ANNOTATION,
        usage,
      ),
    );
    return mapAnnotationDataEntry(data);
  }

  async deleteAnnotationData(
    dataSetId: string,
    annotationSetId: string,
    dataId: string,
    usage?: UsageContextOverrides,
  ): Promise<void> {
    await this.client.delete(
      annotationDataEntryPath(dataSetId, annotationSetId, dataId),
      undefined,
      optionalUsageContextRequestOptions(
        DATASET_USAGE_ACTIONS.DELETE_ANNOTATION,
        usage,
      ),
    );
  }

  // ── Annotation file operations ────────────────────────────────────────

  async annotationCreateUploadUrl(
    dataSetId: string,
    annotationSetId: string,
    dataId: string,
    fileName: string,
    usage?: UsageContextOverrides,
  ): Promise<FileUploadUrlResult> {
    const { data } = await this.client.post<ApiFileUploadUrlResponse>(
      annotationDataFilesPath(dataSetId, annotationSetId, dataId),
      { fileName },
      optionalUsageContextRequestOptions(
        DATASET_USAGE_ACTIONS.UPLOAD_ANNOTATION_FILE,
        usage,
      ),
    );
    return { fileId: data.id, uploadUrl: data.url };
  }

  async annotationCompleteUpload(
    dataSetId: string,
    annotationSetId: string,
    dataId: string,
    request: CompleteFileUploadRequest,
  ): Promise<AnnotationDataEntry> {
    const { data } = await this.client.put<ApiAnnotationData>(
      annotationDataFilesPath(dataSetId, annotationSetId, dataId),
      {
        id: request.id,
        fileName: request.fileName,
        fileType: FILE_TYPE_INT[request.fileType],
        fileSizeInBytes: request.fileSizeInBytes,
        properties: request.properties,
      },
    );
    return mapAnnotationDataEntry(data);
  }

  async annotationGetFileUrls(
    dataSetId: string,
    annotationSetId: string,
    dataId: string,
    usage?: UsageContextOverrides,
  ): Promise<Record<string, string>> {
    const { data } = await this.client.get<Record<string, string>>(
      annotationDataFileUrlsPath(dataSetId, annotationSetId, dataId),
      optionalUsageContextRequestOptions(
        DATASET_USAGE_ACTIONS.DOWNLOAD_ANNOTATION_FILE,
        usage,
      ),
    );
    return data;
  }

  async annotationDeleteFile(
    dataSetId: string,
    annotationSetId: string,
    dataId: string,
    fileId: string,
    usage?: UsageContextOverrides,
  ): Promise<void> {
    await this.client.delete(
      annotationDataFilePath(dataSetId, annotationSetId, dataId, fileId),
      undefined,
      optionalUsageContextRequestOptions(
        DATASET_USAGE_ACTIONS.DELETE_ANNOTATION_FILE,
        usage,
      ),
    );
  }

  async annotationBatchGetDownloadUrls(
    dataSetId: string,
    annotationSetId: string,
    version: string,
    items: BatchDownloadUrlItem[],
    usage?: UsageContextOverrides,
    requireVersionAcknowledgement = false,
  ): Promise<BatchDownloadUrlResult> {
    const { data, headers } =
      await this.client.post<ApiBatchFileDownloadUrlItemBatchResult>(
        annotationBatchDownloadUrlsPath(dataSetId, annotationSetId),
        { version, items },
        // Opt-in: an annotation download issues this single batch call. The
        // caller passes `usage` so the download records exactly one Download
        // Annotations event (D2). The endpoint is a POST, so TEDH's verb-derived
        // category is Create today; we surface it as Download in our taxonomy.
        optionalUsageContextRequestOptions(
          DATASET_USAGE_ACTIONS.DOWNLOAD_ANNOTATIONS,
          usage,
        ),
      );
    if (
      requireVersionAcknowledgement &&
      headers?.get('X-Annotation-Set-Version') !== version
    ) {
      throw new Error('TEDH did not acknowledge the selected file version.');
    }
    return {
      succeededItems: (data.succeededItems ?? []).map((item) => ({
        dataId: item.dataId ?? '',
        files: item.files ?? {},
      })),
      failedItems: (data.failedItems ?? []).map((f) => ({
        dataId: f.dataId ?? undefined,
        fileName: f.fileName ?? undefined,
        errorMessage: f.errorMessage ?? undefined,
        retryable: f.retryable,
      })),
    };
  }

  // ── Batch file operations (dataset-level) ─────────────────────────────

  async batchGetUploadUrls(
    dataSetId: string,
    items: BatchUploadUrlItem[],
  ): Promise<BatchUploadUrlResult> {
    const { data } =
      await this.client.post<ApiBatchFileUploadUrlItemBatchResult>(
        batchFilesPath(dataSetId),
        { items },
      );
    return {
      succeededItems: (data.succeededItems ?? []).map((item) => ({
        dataId: item.dataId,
        files: (item.files ?? []).map((f) => ({
          fileId: f.id,
          uploadUrl: f.url,
        })),
      })),
      failedItems: (data.failedItems ?? []).map((f) => ({
        dataId: f.dataId ?? undefined,
        fileName: f.fileName ?? undefined,
        errorMessage: f.errorMessage ?? undefined,
        retryable: f.retryable,
      })),
    };
  }

  async batchCompleteFiles(
    dataSetId: string,
    items: BatchCompleteFileItem[],
  ): Promise<BatchCreateDataResult> {
    const { data } = await this.client.put<ApiBatchCreateDataResponse>(
      batchFilesPath(dataSetId),
      {
        items: items.map((item) => ({
          dataId: item.dataId,
          files: item.files.map((f) => ({
            id: f.id,
            fileName: f.fileName,
            fileType: f.fileType,
            fileSizeInBytes: f.fileSizeInBytes,
          })),
        })),
      },
    );
    return {
      succeeded: (data.succeededItems ?? []).map(mapDataEntry),
      failed: (data.failedItems ?? []).map(mapBatchFailure),
    };
  }

  async batchGetDownloadUrls(
    dataSetId: string,
    version: string,
    items: BatchDownloadUrlItem[],
    usage?: UsageContextOverrides,
  ): Promise<BatchDownloadUrlResult> {
    const { data } =
      await this.client.post<ApiBatchFileDownloadUrlItemBatchResult>(
        batchDownloadUrlsPath(dataSetId),
        { version, items },
        // Opt-in: a full-dataset download calls this once per batch of entries.
        // The caller passes `usage` on a single representative call so the whole
        // download records exactly one Download Dataset event (D2).
        optionalUsageContextRequestOptions(
          DATASET_USAGE_ACTIONS.DOWNLOAD_DATASET,
          usage,
        ),
      );
    return {
      succeededItems: (data.succeededItems ?? []).map((item) => ({
        dataId: item.dataId ?? '',
        files: item.files ?? {},
      })),
      failedItems: (data.failedItems ?? []).map((f) => ({
        dataId: f.dataId ?? undefined,
        fileName: f.fileName ?? undefined,
        errorMessage: f.errorMessage ?? undefined,
        retryable: f.retryable,
      })),
    };
  }
}
